import React, { useState, useCallback } from 'react';
import Button from '@atlaskit/button/button';
import Heading from '@atlaskit/heading/heading';
import WarningOutlineIcon from '@atlaskit/icon-lab/core/warning-outline';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Anchor from '@atlaskit/primitives/anchor';
import Box from '@atlaskit/primitives/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Pressable from '@atlaskit/primitives/pressable';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	RecommendationCategory,
	RecommendationResource,
	RecommendationStatus,
} from './RecommendationPanelContext.tsx';

const resourcesHeading = defineMessage({
	id: 'admin-pages-performance.performance-recommendations-card.resources-heading',
	defaultMessage: 'Resources',
	description: 'Heading for the resources section in performance recommendations card',
});

export type PerformanceRecommendationsCardProps = {
	id: RecommendationCategory;
	title: string;
	description: string;
	buttonLabel?: string;
	buttonLink?: string;
	onButtonClick?: () => void;
	resources?: RecommendationResource[];
	cardType?: RecommendationStatus;
	isExpanded?: boolean;
	onExpandChange?: (isExpanded: boolean) => void;
	link?: string;
};

// individual card inside each group
export const PerformanceRecommendationsCard = (
	props: PerformanceRecommendationsCardProps,
): React.JSX.Element => {
	const intl = useIntl();
	const {
		id,
		title,
		description,
		buttonLabel,
		onButtonClick,
		resources,
		cardType = 'danger',
		isExpanded: controlledIsExpanded,
		onExpandChange,
		link,
	} = props;

	const [uncontrolledIsExpanded, setUncontrolledIsExpanded] = useState(true);
	const isExpanded =
		controlledIsExpanded !== undefined ? controlledIsExpanded : uncontrolledIsExpanded;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleExpandClick = useCallback(() => {
		const newExpandedState = !isExpanded;
		setUncontrolledIsExpanded(newExpandedState);
		onExpandChange?.(newExpandedState);
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'cfoRecommendationPanel',
				action: 'clicked',
			}),
			{
				groupCategory: id,
			},
		);
	}, [isExpanded, onExpandChange, createAnalyticsEvent, id]);

	const handleButtonClick = useCallback(() => {
		onButtonClick?.();
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'cfoRecommendationResourceActionButton',
				action: 'clicked',
			}),
			{
				groupCategory: id,
			},
		);
	}, [onButtonClick, createAnalyticsEvent, id]);

	const handleResourceClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'cfoRecommendationResourceLink',
				action: 'clicked',
			}),
			{
				groupCategory: id,
			},
		);
	}, [createAnalyticsEvent, id]);

	const getIconForCardType = () => {
		switch (cardType) {
			case 'WARNING':
				return <WarningOutlineIcon color={token('color.icon.warning')} label="" />;
			case 'INFO':
				return <LightbulbIcon color={token('color.icon.information')} label="" />;
			case 'CRITICAL':
			default:
				return <StatusErrorIcon color={token('color.icon.danger')} label="" />;
		}
	};

	return (
		<Stack xcss={cardContainerStyles}>
			{/* Header */}
			<Pressable onClick={handleExpandClick} xcss={headerStyles}>
				<Box xcss={headerLeftStyles}>
					<Box xcss={[iconStyles]}>{getIconForCardType()}</Box>
					<Box xcss={headingWrapperStyles}>
						<Heading as="h4" size="small">
							{title}
						</Heading>
					</Box>
				</Box>
				<Box xcss={chevronStyles}>
					{isExpanded ? (
						<ChevronUpIcon label="" size="medium" />
					) : (
						<ChevronDownIcon label="" size="medium" />
					)}
				</Box>
			</Pressable>
			{/* Divider */}
			<Box xcss={dividerStyles} />
			{/* Body - conditionally rendered */}
			{isExpanded && (
				<Stack xcss={bodyStyles} space="space.200">
					{/* Description */}
					<Text color="color.text.subtle">{description}</Text>

					{/* Button */}
					{buttonLabel && (
						<Box>
							<Button
								appearance="default"
								target="_blank"
								href={link}
								onClick={handleButtonClick}
								iconBefore={<LinkExternalIcon label="" size="small" />}
							>
								{buttonLabel}
							</Button>
						</Box>
					)}
					{/* Resources Section */}
					{resources && resources.length > 0 && (
						<Stack space="space.075">
							<Box as="h5" xcss={resourcesHeadingStyles}>
								{intl.formatMessage(resourcesHeading)}
							</Box>
							<Stack space="space.050" xcss={resourcesListStyles}>
								{resources.map((resource, index) => (
									<Anchor
										key={index}
										href={resource.link}
										target="_blank"
										xcss={resourceLinkStyles}
										onClick={handleResourceClick}
									>
										{resource.name}
										<LinkExternalIcon label="" size="small" />
									</Anchor>
								))}
							</Stack>
						</Stack>
					)}
				</Stack>
			)}
		</Stack>
	);
};

// const warningStyles = xcss({
// 	backgroundColor: 'color.background.accent.orange.subtlest',
// });

// const dangerStyles = xcss({
// 	backgroundColor: 'color.background.accent.red.subtlest',
// });

const cardContainerStyles = xcss({
	borderRadius: token('radius.small'),
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	backgroundColor: 'color.background.neutral.subtle',
	overflow: 'hidden',
});

const headerStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 'space.100',
	width: '100%',
	padding: 'space.200',
	borderTopLeftRadius: 'radius.large',
	borderTopRightRadius: 'radius.large',
	borderBottomRightRadius: '0',
	borderBottomLeftRadius: '0',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	cursor: 'pointer',
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle',
	},
});

const headerLeftStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
	flex: '1 1 auto',
	minWidth: '0',
	textAlign: 'left',
});

const chevronStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
});

const iconStyles = xcss({
	display: 'flex',
	width: '32px',
	height: '32px',
	minWidth: '32px',
	maxWidth: '32px',
	minHeight: '32px',
	maxHeight: '32px',
	padding: 'space.050',
	justifyContent: 'center',
	alignItems: 'center',
	gap: 'space.100',
	flexShrink: 0,
	borderRadius: 'radius.tile',
	backgroundColor: 'color.background.neutral',
});

const dividerStyles = xcss({
	height: '1px',
	backgroundColor: 'color.background.neutral',
	width: '100%',
});

const bodyStyles = xcss({
	padding: 'space.200',
	textAlign: 'left',
});

const resourcesHeadingStyles = xcss({
	margin: '0',
});

const resourcesListStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
});

const resourceLinkStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.050',
	color: 'color.link',
	textDecoration: 'none',
	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

const headingWrapperStyles = xcss({
	flex: '1 1 auto',
	minWidth: '0',
	textAlign: 'left',
});
